import React, { useEffect, useMemo, useState } from 'react'

import styles from './BankAccount.module.scss'

import BackButton from '../../components/buttons/BackButton/BackButton'
import { useBankAccount, useCreateBankAccount, useUpdateBankAccount, useUser } from '../../hooks'

const BANK_OPTIONS = [
	{ name: 'IBK기업은행', code: '003' },
	{ name: 'KB국민은행', code: '004' },
	{ name: 'NH농협은행', code: '011' },
	{ name: '우리은행', code: '020' },
	{ name: 'SC제일은행', code: '023' },
	{ name: 'iM뱅크(대구은행)', code: '031' },
	{ name: '부산은행', code: '032' },
	{ name: '광주은행', code: '034' },
	{ name: '경남은행', code: '045' },
	{ name: '새마을금고', code: '039' },
	{ name: '우체국', code: '071' },
	{ name: '하나은행(KEB하나은행)', code: '081' },
	{ name: '신한은행', code: '088' },
	{ name: '케이뱅크', code: '089' },
]

const INITIAL_FORM = {
	bankName: '',
	bankCode: '',
	accountNumber: '',
	ownerName: '',
}

const BankAccount = () => {
	const { data: user } = useUser()

	const {
		data: accountData,
		isLoading: isAccountLoading,
		isError: isAccountError,
		error: accountError,
		refetch: refetchAccount,
	} = useBankAccount()
	const createMutation = useCreateBankAccount()
	const updateMutation = useUpdateBankAccount()

	const [form, setForm] = useState(INITIAL_FORM)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)

	const isSubmitting = createMutation.isPending || updateMutation.isPending
	const hasExistingAccount = useMemo(
		() =>
			Boolean(accountData?.accountNumber || accountData?.bankName || accountData?.ownerName),
		[accountData],
	)

	useEffect(() => {
		console.log('accountData', accountData)
		if (accountData) {
			setForm({
				bankName: accountData.bankName ?? '',
				bankCode: accountData.bankCode ?? '',
				accountNumber: accountData.accountNumber ?? '',
				ownerName: accountData.ownerName ?? user?.name ?? '',
			})
			return
		}

		setForm((prev) => ({
			...prev,
			ownerName: user?.name ?? '',
		}))
	}, [accountData, user])

	const handleSelectBank = (event, bank) => {
		event.stopPropagation()
		setForm((prev) => ({ ...prev, bankName: bank.name, bankCode: bank.code }))
		setIsDropdownOpen(false)
	}

	const handleChange = (key) => (event) => {
		setForm((prev) => ({
			...prev,
			[key]: event.target.value,
		}))
	}

	const isCaddie = user?.userType === 'tourCaddie'

	const handleSubmit = async (event) => {
		event.preventDefault()

		if (!form.bankName || !form.ownerName || !form.accountNumber) {
			alert('은행, 예금주, 계좌번호를 모두 입력해 주세요.')
			return
		}

		const payload = {
			bankName: form.bankName,
			bankCode: form.bankCode,
			accountNumber: form.accountNumber.replace(/\s+/g, ''),
			ownerName: form.ownerName,
		}

		try {
			if (hasExistingAccount) {
				await updateMutation.mutateAsync(payload)
			} else {
				await createMutation.mutateAsync(payload)
			}

			await refetchAccount()
			alert('계좌 정보가 저장되었습니다.')
		} catch (error) {
			console.error('Failed to save bank account:', error)
			alert('계좌 정보를 저장하지 못했어요. 다시 시도해 주세요.')
			return
		}

		setIsDropdownOpen(false)
	}

	if (!isCaddie) {
		return (
			<div className={styles.bankAccountWrapper}>
				<BackButton />
				<div className={styles.restrictionNotice}>
					<p>계좌 관리는 캐디 전용 기능입니다.</p>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.bankAccountWrapper}>
			<BackButton />
			<form className={styles.form} onSubmit={handleSubmit}>
				<div className={styles.header}>
					<h2 className={styles.title}>정산 계좌 관리</h2>
					<p className={styles.subtitle}>
						정산 시 사용할 계좌 정보를 등록해 주세요. 등록된 계좌로 정산금이 지급됩니다.
					</p>
				</div>

				{isAccountLoading && (
					<div className={styles.noticeBox}>
						<span>계좌 정보를 불러오고 있어요...</span>
					</div>
				)}

				{isAccountError && (
					<div className={styles.errorBox}>
						<p>계좌 정보를 불러오지 못했습니다. 다시 시도해 주세요.</p>
						<p className={styles.errorDetail}>{accountError?.message}</p>
					</div>
				)}

				<div className={styles.fieldGroup}>
					<label className={styles.fieldLabel}>은행명</label>
					<div
						className={styles.bankSelect}
						onClick={() => setIsDropdownOpen((prev) => !prev)}>
						<span className={styles.bankSelectValue}>
							{form.bankName || '은행을 선택해 주세요.'}
						</span>
						<ChevronIcon isOpen={isDropdownOpen} />
						{isDropdownOpen && (
							<div className={styles.bankSelectDropdown}>
								{BANK_OPTIONS.map((bank) => (
									<button
										type="button"
										key={bank.code}
										className={styles.bankSelectItem}
										onClick={(event) => handleSelectBank(event, bank)}>
										{bank.name}
									</button>
								))}
							</div>
						)}
					</div>
				</div>

				<div className={styles.fieldGroup}>
					<label className={styles.fieldLabel}>예금주</label>
					<input
						className={styles.input}
						placeholder="예금주 이름을 입력하세요."
						value={form.ownerName}
						onChange={handleChange('ownerName')}
						maxLength={30}
					/>
				</div>

				<div className={styles.fieldGroup}>
					<label className={styles.fieldLabel}>계좌번호</label>
					<input
						className={styles.input}
						placeholder="계좌번호를 입력하세요. (- 없이 숫자만)"
						value={form.accountNumber}
						onChange={handleChange('accountNumber')}
						maxLength={30}
					/>
				</div>

				<div className={styles.noticeBox}>
					<ul>
						<li>정확한 계좌 정보를 입력하지 않으면 정산이 지연될 수 있습니다.</li>
						<li>계좌 변경 시 반드시 저장 버튼을 눌러 정보를 갱신해 주세요.</li>
					</ul>
				</div>

				<div className={styles.actionRow}>
					<button type="submit" className={styles.submitButton} disabled={isSubmitting}>
						{isSubmitting
							? '저장 중...'
							: hasExistingAccount
								? '계좌 정보 수정'
								: '계좌 정보 등록'}
					</button>
				</div>
			</form>
		</div>
	)
}

const ChevronIcon = ({ isOpen }) => (
	<span className={styles.chevronIcon} data-open={isOpen}>
		▾
	</span>
)

export default BankAccount
